import {
  Card,
  CardContent,
  TextField,
  Typography,
  Stack,
  Button,
  Alert,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import type React from 'react'
import { useContext, useEffect, useState } from 'react'

import type { SubjectData } from '@/components/Requests/types'
import type { ctxType } from '@/context/AppContext'
import AppContext from '@/context/AppContext'
import { graphQLQuery } from '@/hooks/hooks'
import { useCreateDataRequestMutation } from '@/lib/apollo/mutation/createDataRequest'
import QUERIES from '@/lib/graphql'
import type { Income } from '@/types/Activity'
import { RequestType } from '@/types/Recommendation'

type RequestStatus = 'VALID' | 'INVALID' | null

export interface RequestFormProps {
  subjectData: SubjectData
  requestType: RequestType
  incomes?: Income[]
  onSuccess: () => void
  onSubmitStart: () => void
  onSubmitEnd: () => void
  onError: () => void
}

const DEFAULT_MESSAGES = {
  [RequestType.OPEN_BANKING]:
    'Our referencing team has requested an Open Banking connection to aid your Goodlord application.',
  [RequestType.PAYROLL]:
    'Our referencing team has requested a Payroll/HMRC connection to aid your Goodlord application.',
}

const capitalise = (value: string) => `${value.charAt(0).toUpperCase()}${value.slice(1)}`

const formatIncomeAmount = (income: Income) => {
  const { value, currency, frequency } = income.incomeProperties

  const amount =
    value === null || value === undefined
      ? null
      : `${currency?.symbol ?? ''}${value.toLocaleString('en-GB', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`

  return [amount, frequency].filter(Boolean).join(' ')
}

const formatIncomeOptionLabel = (income: Income, index: number) =>
  `Income ${index + 1} - ${capitalise(income.type)} - ${formatIncomeAmount(income)}`

const getRequestMessage = (requestType: RequestType, income?: Income) => {
  const baseMessage = DEFAULT_MESSAGES[requestType]

  if (!income) {
    return baseMessage
  }

  return `${baseMessage} This is for your ${capitalise(income.type)} Income of ${formatIncomeAmount(income)}.`
}

export const RequestForm: React.FC<RequestFormProps> = ({
  subjectData,
  requestType,
  incomes = [],
  onSuccess,
  onSubmitStart,
  onSubmitEnd,
  onError,
}) => {
  const isOpenBanking = requestType === RequestType.OPEN_BANKING

  const [selectedIncomeId, setSelectedIncomeId] = useState<string>('')
  const selectedIncome = incomes.find(income => income.id === selectedIncomeId)
  const [customMessage, setCustomMessage] = useState(
    getRequestMessage(requestType, selectedIncome),
  )
  const [sending, setSending] = useState(false)
  const [status, setStatus] = useState<RequestStatus>(null)
  const { personId } = useContext<ctxType>(AppContext)

  const [createDataRequest] = useCreateDataRequestMutation()

  const defaultRequestSubject = 'Action Required: Complete your Payroll/HMRC Verification'

  useEffect(() => {
    setCustomMessage(getRequestMessage(requestType, selectedIncome))
    setStatus(null)
  }, [requestType, selectedIncome])

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault()
    }
    if (sending) return

    setSending(true)
    onSubmitStart()

    try {
      if (isOpenBanking) {
        const response = await graphQLQuery({
          query: QUERIES.addOpenBankingRequest,
          variables: {
            ...subjectData,
            requestMessage: customMessage,
            personId,
            incomeId: selectedIncomeId,
          },
        })

        await response.json()
        setStatus('VALID')
        onSuccess()
      } else {
        await createDataRequest({
          variables: {
            customerId: subjectData.customerId,
            subjectId: subjectData.subjectId,
            personId: personId ?? '',
            requestSubject: defaultRequestSubject,
            requestMessage: customMessage,
            recommendationType: 'PayrollRecommendation',
          },
        })

        setStatus('VALID')
        onSuccess()
      }
    } catch {
      setStatus('INVALID')
      onError()
    } finally {
      setSending(false)
      onSubmitEnd()
    }
  }

  const disabled =
    sending || !subjectData.isRefCompleted || (isOpenBanking && !selectedIncomeId)

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Card
        variant="outlined"
        sx={{
          mb: 2,
          borderColor: '#666',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        <CardContent>
          <Stack spacing={3}>
            <Typography variant="h6">
              {isOpenBanking
                ? 'Request New Open Banking Connection'
                : 'Request New Payroll/HMRC Connection'}
            </Typography>

            <Stack spacing={2}>
              <Typography variant="body2">Hi {subjectData.name}</Typography>

              {isOpenBanking && (
                <FormControl fullWidth required>
                  <InputLabel id="request-income-label" shrink>
                    Income
                  </InputLabel>
                  <Select
                    labelId="request-income-label"
                    id="request-income"
                    label="Income"
                    displayEmpty
                    value={selectedIncomeId}
                    onChange={event => setSelectedIncomeId(event.target.value)}
                  >
                    <MenuItem value="" disabled>
                      Select an income
                    </MenuItem>
                    {incomes.map((income, index) => (
                      <MenuItem key={income.id} value={income.id}>
                        {formatIncomeOptionLabel(income, index)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <TextField
                multiline
                rows={6}
                value={customMessage}
                onChange={event => setCustomMessage(event.target.value)}
                placeholder="Enter your message..."
                fullWidth
              />

              <Typography variant="body2">
                Regards
                <br />
                Team Goodlord
              </Typography>
            </Stack>

            {status && (
              <Alert severity={status === 'VALID' ? 'success' : 'error'}>
                {status === 'VALID'
                  ? 'Request successfully sent!'
                  : 'Error: Request failed to send.'}
              </Alert>
            )}

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="flex-end"
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <Button
                variant="contained"
                type="submit"
                disabled={disabled}
                sx={{
                  px: 4,
                  py: 2,
                  borderRadius: 2,
                  bgcolor: '#1B4554',
                  boxShadow: 3,
                  '&:hover': {
                    bgcolor: '#12303A',
                    boxShadow: 6,
                  },
                }}
              >
                {sending ? 'Sending...' : 'Send request'}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  )
}

export default RequestForm
